import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';

import type { PermissionVocabularyViewModel } from '../api';
import { assemblePermission, builderInputs, defaultSelection } from '../permissionBuilder';
import { formatPermissionLabel } from '../permissionFormat';

/** Title-case a template placeholder for its field label: `collection` → `Collection`. */
function titleCase(value: string): string {
  return value.length === 0 ? value : value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * The enum-driven grammar builder (PTV2-1443): pick a canonical shape, then one dropdown per fillable
 * slot — every option from the vocabulary, nothing hardcoded — with a live preview of the assembled
 * `a:b:c:d:e` string. Because the inputs come from the shape template, only structurally valid
 * permissions can be produced. Emits the assembled string via {@link onAdd}.
 */
export function PermissionGrammarBuilder({
  vocabulary,
  onAdd,
  disabled = false,
}: {
  readonly vocabulary: PermissionVocabularyViewModel;
  readonly onAdd: (permission: string) => void;
  readonly disabled?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const shapes = vocabulary.shapes;
  const [shapeId, setShapeId] = useState<string>(shapes[0]?.id ?? '');

  const shape = useMemo(
    () => shapes.find((s) => s.id === shapeId) ?? shapes[0],
    [shapes, shapeId],
  );
  const inputs = useMemo(
    () => (shape === undefined ? [] : builderInputs(shape, vocabulary)),
    [shape, vocabulary],
  );

  const [selected, setSelected] = useState<Record<string, string>>(() => defaultSelection(inputs));

  // Reset the per-slot selection whenever the active shape (and thus its inputs) changes.
  useEffect(() => {
    setSelected(defaultSelection(inputs));
  }, [inputs]);

  if (shape === undefined) {
    return <p className="text-sm text-muted-foreground">{t('routes.platform.detail.roles.permissions.builder.unavailable')}</p>;
  }

  const assembled = assemblePermission(shape, selected);

  return (
    <div className="space-y-3 rounded-md border border-border bg-muted/30 p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="space-y-1 text-sm">
          <span className="font-medium text-foreground">
            {t('routes.platform.detail.roles.permissions.builder.shape')}
          </span>
          <Select value={shapeId} onChange={(e) => { setShapeId(e.target.value); }} disabled={disabled}>
            {shapes.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </Select>
        </label>
        {inputs.map((input) => (
          <label key={input.placeholder} className="space-y-1 text-sm">
            <span className="font-medium text-foreground">{titleCase(input.placeholder)}</span>
            <Select
              value={selected[input.placeholder] ?? ''}
              onChange={(e) => {
                const { value } = e.target;
                setSelected((prev) => ({ ...prev, [input.placeholder]: value }));
              }}
              disabled={disabled}
            >
              {input.options.map((term) => (
                <option key={term.value} value={term.value}>{term.label}</option>
              ))}
            </Select>
          </label>
        ))}
      </div>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm text-foreground" title={assembled}>
            {formatPermissionLabel(assembled, vocabulary)}
          </p>
          <code className="text-xs text-muted-foreground">{assembled}</code>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled}
          onClick={() => { onAdd(assembled); }}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('routes.platform.detail.roles.permissions.builder.add')}
        </Button>
      </div>
    </div>
  );
}
